import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  User, 
  LogOut, 
  Crown, 
  BarChart2, 
  GraduationCap,
  Menu,
  Play,
  Trophy,
  Medal,
  BookOpen,
  Flame,
  Gift,
  Sun,
  Moon,
  Database,
  Wand2,
  Video,
  CheckSquare,
  Camera,
  MoreHorizontal,
  ChevronDown,
} from 'lucide-react';
import { IS_ANDROID_APP } from '../utils/platform';

const MORE_LINKS = [
  { to: '/lessons', label: 'Lessons', Icon: Video, testId: 'nav-lessons' },
  { to: '/solver', label: 'Solver', Icon: Wand2, testId: 'nav-solver' },
  { to: '/validator', label: 'Validator', Icon: CheckSquare, testId: 'nav-validator' },
  { to: '/leaderboard', label: 'Leaderboard', Icon: Trophy, testId: 'nav-leaderboard' },
  { to: '/achievements', label: 'Badges', Icon: Medal, testId: 'nav-achievements' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [flagQueue, setFlagQueue] = useState(0);

  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    const load = () => axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/flag-queue/count`, { withCredentials: true })
      .then((r) => setFlagQueue(r.data?.open || 0))
      .catch(() => {});
    load();
    window.addEventListener('flag-queue-changed', load);
    return () => window.removeEventListener('flag-queue-changed', load);
  }, [user?.role]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="border-b border-[var(--border-subtle)] px-4 py-4" style={{ backgroundColor: 'var(--background-main)' }}>
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-8 h-8 rounded bg-[#8B5A3C] flex items-center justify-center">
            <span className="font-mono text-white font-bold">9</span>
          </div>
          <span className="font-heading text-xl font-bold text-[var(--text-primary)] hidden sm:block">
            Sudoku Academy
          </span>
        </Link>

        <div className="flex items-center gap-4">
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/play" 
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-2"
              data-testid="nav-play"
            >
              <Play className="w-4 h-4" />
              Play
            </Link>
            <Link 
              to="/daily" 
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-2"
              data-testid="nav-daily"
            >
              <Flame className="w-4 h-4" />
              Classic Sudoku
            </Link>
            <Link 
              to="/scan" 
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-2"
              data-testid="nav-scan"
              title="Bring your own puzzle: type it in and play"
            >
              <Camera className="w-4 h-4" />
              BYO
            </Link>
            <Link 
              to="/tutorials" 
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-2"
              data-testid="nav-tutorials"
            >
              <GraduationCap className="w-4 h-4" />
              Tutorials
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-1.5 outline-none"
                  data-testid="nav-more"
                >
                  <MoreHorizontal className="w-4 h-4" />
                  More
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[var(--background-surface)] border-[var(--border-subtle)] w-48">
                {MORE_LINKS.map(({ to, label, Icon, testId }) => (
                  <DropdownMenuItem
                    key={to}
                    onClick={() => navigate(to)}
                    className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
                    data-testid={testId}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {label}
                  </DropdownMenuItem>
                ))}
                {process.env.REACT_APP_DISCORD_INVITE_URL && (
                  <DropdownMenuItem asChild className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer">
                    <a href={process.env.REACT_APP_DISCORD_INVITE_URL} target="_blank" rel="noreferrer noopener" data-testid="nav-discord">
                      <DiscordIcon />
                      <span className="ml-2">Community</span>
                    </a>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Streak indicator (only when user has an active streak) */}
          <StreakBadge streak={user?.stats?.current_streak || 0} onClick={() => navigate('/achievements')} />

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-colors"
            data-testid="theme-toggle"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
                  data-testid="user-menu-btn"
                >
                  <div className="relative w-8 h-8 rounded-full bg-[#8B5A3C]/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#8B5A3C]" />
                    {flagQueue > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#FF3B30] ring-2 ring-[var(--background-main)]" data-testid="flag-queue-dot" />
                    )}
                  </div>
                  <span className="hidden sm:block">{user.name || user.username || user.email}</span>
                  {user.founding_member ? (
                    <span
                      className="flex items-center gap-1 rounded-full bg-[var(--hover-bg)] border border-[var(--accent-primary)] text-[var(--accent-primary)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                      data-testid="founding-member-badge"
                      title="Founding Member — lifetime Premium"
                    >
                      <Crown className="w-3 h-3" />
                      <span className="hidden sm:inline">Founding Member</span>
                    </span>
                  ) : user.subscription_tier !== 'free' && (
                    <Crown className="w-4 h-4 text-[#FF9F0A]" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-56 bg-[var(--background-surface)] border-[var(--border-subtle)]"
              >
                <DropdownMenuItem 
                  onClick={() => navigate('/dashboard')}
                  className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
                  data-testid="menu-dashboard"
                >
                  <BarChart2 className="w-4 h-4 mr-2" />
                  Dashboard
                </DropdownMenuItem>
                {!IS_ANDROID_APP && <DropdownMenuItem 
                  onClick={() => navigate('/subscription')}
                  className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
                  data-testid="menu-subscription"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Subscription
                </DropdownMenuItem>}
                <DropdownMenuItem 
                  onClick={() => navigate('/referral')}
                  className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
                  data-testid="menu-referral"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Invite Friends
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <>
                    <DropdownMenuSeparator className="bg-[var(--border-subtle)]" />
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      className="text-[#8B5A3C] hover:bg-[var(--hover-bg)] cursor-pointer"
                      data-testid="menu-admin"
                    >
                      <Database className="w-4 h-4 mr-2" />
                      Admin
                      {flagQueue > 0 && (
                        <span
                          className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-semibold flex items-center justify-center"
                          data-testid="flag-queue-badge"
                          title={`${flagQueue} flagged puzzle${flagQueue === 1 ? '' : 's'} to review`}
                        >
                          {flagQueue}
                        </span>
                      )}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator className="bg-[var(--border-subtle)]" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-[#FF3B30] hover:bg-[var(--hover-bg)] cursor-pointer"
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
                data-testid="nav-login"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-[#8B5A3C] hover:bg-[#A46944] text-white"
                data-testid="nav-register"
              >
                Get Started
              </Button>
            </div>
          )}

          {/* Mobile Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)]">
                <Menu className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-56 bg-[var(--background-surface)] border-[var(--border-subtle)] md:hidden"
            >
              <DropdownMenuItem 
                onClick={() => navigate('/play')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
              >
                <Play className="w-4 h-4 mr-2" />
                Play
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/daily')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
              >
                <Flame className="w-4 h-4 mr-2" />
                Classic Sudoku
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/tutorials')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Tutorials
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/lessons')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
                data-testid="nav-lessons-mobile"
              >
                <Video className="w-4 h-4 mr-2" />
                Lessons
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/scan')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
                data-testid="nav-scan-mobile"
              >
                <Camera className="w-4 h-4 mr-2" />
                BYO (Type &amp; Play)
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/leaderboard')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Leaderboard
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/achievements')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
              >
                <Medal className="w-4 h-4 mr-2" />
                Badges
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/science')}
                className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
                data-testid="nav-science-mobile"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Science &amp; History
              </DropdownMenuItem>
              {process.env.REACT_APP_DISCORD_INVITE_URL && (
                <DropdownMenuItem
                  onClick={() => window.open(process.env.REACT_APP_DISCORD_INVITE_URL, '_blank', 'noopener,noreferrer')}
                  className="text-[var(--text-primary)] hover:bg-[var(--hover-bg)] cursor-pointer"
                  data-testid="nav-discord-mobile"
                >
                  <DiscordIcon className="w-4 h-4 mr-2" />
                  Community
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}

function StreakBadge({ streak = 0, onClick }) {
  if (streak < 1) return null;
  // Tier colors matching Streaks category / lucide Flame
  let accent = '#FF9F0A';       // 1-2 days: amber
  if (streak >= 3) accent = '#FF3B30';   // 3+: red
  if (streak >= 7) accent = '#FF2D92';   // 7+: hot pink
  if (streak >= 30) accent = '#FFD60A';  // 30+: gold

  return (
    <button
      onClick={onClick}
      className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-full text-xs font-mono font-bold transition-all hover:scale-105"
      style={{
        color: accent,
        backgroundColor: `${accent}15`,
        border: `1px solid ${accent}40`,
      }}
      title={`${streak}-day streak — keep it going!`}
      data-testid="nav-streak"
    >
      <Flame className="w-3.5 h-3.5" fill={accent} strokeWidth={1.5} />
      {streak}
    </button>
  );
}

function DiscordIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.249a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.036A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}
